/**
 * SaveTheDateBanner — Banner sticky in cima al sito (FASE 4.2.11 TASK 3).
 *
 * Visibilita': solo se esiste un evento con
 *   data_inizio - 3gg <= oggi <= data_fine (incluso)
 *
 * Sorgente: GET /api/public/eventi/save-the-date
 *
 * Dismissable per evento (localStorage). Si nasconde da solo quando l'evento finisce.
 */
import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, X, ArrowRight } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const LS_KEY = "colucci_save_the_date_dismissed";
const BANNER_VAR = "--colucci-banner-h";

function formatItalianDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "numeric", month: "long" });
  } catch (_) { return iso; }
}

export default function SaveTheDateBanner() {
  const [evento, setEvento] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const bannerRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/public/eventi/save-the-date`, { withCredentials: true })
      .then((r) => {
        const ev = r.data?.evento || null;
        if (!ev) return;
        // dismissed per questo specifico evento?
        try {
          const stored = localStorage.getItem(LS_KEY);
          if (stored && stored === ev.slug) {
            setDismissed(true);
            return;
          }
        } catch (_) { /* localStorage indisponibile */ }
        setEvento(ev);
      })
      .catch(() => setEvento(null));
  }, []);

  // Misura altezza banner e pubblicala su :root come CSS var (--colucci-banner-h).
  // Permette a <Header> di offsettarsi e a <body> di padd-top-tare senza overlap.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!evento || dismissed) {
      root.style.setProperty(BANNER_VAR, "0px");
      return;
    }
    const el = bannerRef.current;
    if (!el) return undefined;
    const apply = () => root.style.setProperty(BANNER_VAR, `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", apply);
      root.style.setProperty(BANNER_VAR, "0px");
    };
  }, [evento, dismissed]);

  if (!evento || dismissed) return null;

  const handleDismiss = () => {
    try { localStorage.setItem(LS_KEY, evento.slug); } catch (_) {}
    setDismissed(true);
  };

  const di = evento.data_inizio;
  const df = evento.data_fine;
  const isInProgress = evento.status_label === "in_corso";
  const titolo = (evento.titolo || "").split("×")[0].trim() || evento.titolo;

  return (
    <div
      ref={bannerRef}
      className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 text-white text-sm shadow-lg"
      data-testid="save-the-date-banner"
      role="region"
      aria-label="Save the Date evento Colucci"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3 flex-wrap">
        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white/15 text-[10px] font-bold uppercase tracking-wider" data-testid="save-the-date-status">
          <Calendar className="w-3 h-3" />
          {isInProgress ? "In corso ora" : "Save the date"}
        </span>
        <span className="font-medium" data-testid="save-the-date-text">
          {isInProgress ? "Siamo a" : "Vieni a trovarci a"}{" "}
          <strong>{titolo}</strong>
          {di && df && (
            <span className="text-white/85">
              {" "}· dal {formatItalianDate(di)} al {formatItalianDate(df)}
            </span>
          )}
        </span>
        <Link
          to={`/eventi/${evento.slug}`}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-emerald-700 text-xs font-semibold hover:bg-emerald-50 transition-colors"
          data-testid="save-the-date-cta"
        >
          Scopri di più <ArrowRight className="w-3 h-3" />
        </Link>
        <button
          type="button"
          onClick={handleDismiss}
          className="text-white/80 hover:text-white transition-colors"
          aria-label="Nascondi banner Save the Date"
          data-testid="save-the-date-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
